import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, DollarSign, Calendar, FileText } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const BillDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBillDetails();
  }, [id]);

  const fetchBillDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/bills/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBill(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch bill details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      unpaid: 'bg-yellow-100 text-yellow-800',
      partially_paid: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !bill) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || 'Bill not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/accounting/bills')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bill {bill.bill_number}</h1>
            <p className="text-sm text-gray-500">Vendor: {bill.vendor_name}</p>
          </div>
        </div>
        <span
          className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(bill.status)}`}
        >
          {bill.status ? bill.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
        </span>
      </div>

      {/* Bill Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4">Bill Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Bill Number</dt>
                <dd className="text-sm font-medium text-gray-900">{bill.bill_number}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Bill Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(bill.bill_date)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Due Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(bill.due_date)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Terms</dt>
                <dd className="text-sm text-gray-900 capitalize">
                  {bill.terms?.replace('_', ' ')}
                </dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4">Vendor Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Vendor</dt>
                <dd className="text-sm font-medium text-gray-900">{bill.vendor_name}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Vendor Code</dt>
                <dd className="text-sm text-gray-900">{bill.vendor_code}</dd>
              </div>
              {bill.vendor_phone && (
                <div>
                  <dt className="text-sm text-gray-500">Phone</dt>
                  <dd className="text-sm text-gray-900">{bill.vendor_phone}</dd>
                </div>
              )}
              {bill.vendor_email && (
                <div>
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">{bill.vendor_email}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Bill Lines */}
      {bill.lines && bill.lines.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Account
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Unit Cost
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bill.lines.map((line, index) => (
                  <tr key={line.id || index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {line.line_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{line.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {line.account_code} - {line.account_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {parseFloat(line.quantity || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(line.unit_cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatCurrency(line.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Amount Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Amount Summary</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Subtotal</dt>
            <dd className="text-sm font-medium text-gray-900">{formatCurrency(bill.subtotal)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Tax</dt>
            <dd className="text-sm font-medium text-gray-900">{formatCurrency(bill.tax_amount)}</dd>
          </div>
          <div className="flex justify-between pt-3 border-t border-gray-200">
            <dt className="text-base font-medium text-gray-900">Total Amount</dt>
            <dd className="text-base font-bold text-gray-900">
              {formatCurrency(bill.total_amount)}
            </dd>
          </div>
          {parseFloat(bill.amount_paid || 0) > 0 && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Amount Paid</dt>
              <dd className="text-sm font-medium text-green-600">
                {formatCurrency(bill.amount_paid)}
              </dd>
            </div>
          )}
          <div className="flex justify-between pt-3 border-t border-gray-200">
            <dt className="text-base font-medium text-gray-900">Balance Due</dt>
            <dd
              className={`text-base font-bold ${parseFloat(bill.balance_due) > 0 ? 'text-red-600' : 'text-green-600'}`}
            >
              {formatCurrency(bill.balance_due)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Description & Notes */}
      {(bill.description || bill.notes) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {bill.description && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
              <p className="text-sm text-gray-900">{bill.description}</p>
            </div>
          )}
          {bill.notes && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{bill.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {isAdmin && bill.status !== 'paid' && (
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/accounting/bills/${id}/payment`)}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
          >
            <DollarSign className="h-5 w-5" />
            Record Payment
          </button>
        </div>
      )}
    </div>
  );
};

export default BillDetailPage;
